import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getUserSubscriptionsAsync } from '@/apis/subscriptions.api'
import router, {menuRoutesEnum} from '@/router'
import { useAgentsStore } from './agents.store'
import { usePhonebookStore } from './phonebook.store'
import parser from 'cron-parser'

import * as authApi from '@/apis/auth.api'
import * as scheduledApi from '@/apis/scheduled.api'
import * as agentsApi from '@/apis/agents.api'
import * as portalApi from '@/apis/portal.api'

export const useWebSiteStore = defineStore('website', () => {
  // version
  const version = ref(null)
  // auth
  const accessToken = ref(null)
  const refreshToken = ref(null)
  const decodedToken = ref(null)
  const refreshTokenTimeout = ref(null)
  const refreshTokenLock = ref(null)
  // user and related
  const userData = ref({})
  const userLimits = ref({})
  const viewInfoBanner = ref(true)
  // subscriptions and related
  const subscriptions = ref([])
  // agents and related
  const agents = ref([])
  const agent_topics = ref([])
  const agent_languages = ref([])
  const max_phone_number_changes = ref(0)
  // scheduled messages and related
  const scheduledMessages = ref([])
  const messageLimits = ref({ current: 0, total: 0 })

  const hasActiveSubscription = computed(() => {
    return subscriptions.value.some(s => ['active', 'trialing', 'past_due'].includes(s.status))
  })

  const activeSubscription = computed(() => {
    return subscriptions.value.find(s => ['active', 'trialing', 'past_due'].includes(s.status)) || null
  })

  const pausedSubscription = computed(() => {
    return subscriptions.value.find(s => s.status === 'paused') || null
  })

  const hasWarmupService = computed(() => {
    if (!subscriptions.value || subscriptions.value.length === 0) return false

    return subscriptions.value.some(subscription => {
      const isValidStatus = ['active', 'trialing', 'past_due'].includes(subscription.status)

      return isValidStatus && subscription.has_warmup
    })
  })

  const hasSchedulingService = computed(() => {
    if (!subscriptions.value || subscriptions.value.length === 0) return false

    return subscriptions.value.some(subscription => {
      const isValidStatus = ['active', 'trialing', 'past_due'].includes(subscription.status)

      return isValidStatus && subscription.has_scheduling
    })
  })

  const isPastDueSubscription = computed(() => {
    return subscriptions.value.some(s => s.status === 'past_due')
  })

  const isPausedSubscription = computed(() => {
    return subscriptions.value.some(s => s.status === 'paused')
  })

  const $reset = () => {
    accessToken.value = null
    decodedToken.value = null
    refreshToken.value = null
    refreshTokenTimeout.value = null
    refreshTokenLock.value = null
    userData.value = {}
    userLimits.value = {}
    subscriptions.value = []
    agents.value = []
    agent_topics.value = []
    agent_languages.value = []
    max_phone_number_changes.value = 0
    scheduledMessages.value = []
    messageLimits.value = { current: 0, total: 0 }

    clearRefreshTokenTimer()
  }

  const menuItems = () => {
    return [
    {
      id: 1,
      label: 'nav.dashboard',
      icon: 'mdi-home',
      route: [menuRoutesEnum.HOME],
      onClick: () => goTo(menuRoutesEnum.HOME)
    },
    {
      id: 6,
      label: 'nav.agents',
      icon: 'mdi-robot',
      route: [menuRoutesEnum.AGENTS],
      onClick: () => goTo(menuRoutesEnum.AGENTS),
      // canAccess: hasWarmupService.value
      canAccess: hasActiveSubscription.value
    },
    {
      id: 5,
      label: 'nav.phonebook',
      icon: 'mdi-book-account',
      route: [menuRoutesEnum.PHONEBOOK],
      onClick: () => goTo(menuRoutesEnum.PHONEBOOK)
    },
    {
      id: 4,
      label: 'nav.scheduledMessages',
      icon: 'mdi-calendar-clock',
      route: [menuRoutesEnum.SCHEDULED],
      onClick: () => goTo(menuRoutesEnum.SCHEDULED),
      canAccess: hasSchedulingService.value
    },
    {
      id: 3,
      label: 'nav.user',
      icon: 'mdi-account',
      route: [menuRoutesEnum.USER],
      onClick: () => goTo(menuRoutesEnum.USER)
    },
    // Might need a settings page later, components are already wired
    // {
    //   id: 2,
    //   label: 'nav.settings',
    //   icon: 'mdi-cog',
    //   route: [menuRoutesEnum.SETTINGS],
    //   onClick: () => goTo(menuRoutesEnum.SETTINGS)
    // },
    ]
  }

  const goTo = (route) => {
    router.push(route)
  }

  const logoutAsync = async(userLogout = true) => {
    const agentsStore = useAgentsStore()
    const phonebookStore = usePhonebookStore()

    if (userLogout && isLogged()) {
      await authApi.logoutAsync({refresh: refreshToken.value})
    }

    agentsStore.clear()
    phonebookStore.clear()
    $reset()

    return router.push(menuRoutesEnum.LOGIN)
  }

  const acquireRefreshTokenLock = () => (refreshTokenLock.value = new Date().getTime())
  const releaseRefreshTokenLock = () => (refreshTokenLock.value = null)

  const isLogged = () => {
    const isLogged = !!(accessToken.value && refreshToken.value)
    return isLogged
  }

  const isTokenExpired = () => {
    if (!decodedToken.value) {
      decodedToken.value = decodeToken()
    }
    return !!(decodedToken.value && ((decodedToken.value.exp * 1000) < (Date.now() + 60_000)))
  }

  const updateToken = (accessTokenValue, refreshTokenValue) => {
    accessToken.value = accessTokenValue
    refreshToken.value = refreshTokenValue

    decodedToken.value = decodeToken()
  }

  const decodeToken = (token = accessToken.value) => JSON.parse(atob(token.split('.')[1]))

  const isValidToken = (token, isRefresh = false) => {
    if (isRefresh) {
      return token && token.access
    }
    return token && token.access && token.refresh
  }

  const setTokenAsync = async(token, isRefresh = false) => {
    if (!isValidToken(token, isRefresh)) {
      return logoutAsync()
    }
    updateToken(token.access, token.refresh)

    await startRefreshTokenTimerAsync()
  }

  const startRefreshTokenTimerAsync = async() => {
    clearRefreshTokenTimer()

    const timeout = Math.max((decodedToken.value.exp * 1000) - Date.now() - 10_000, 1000)
    refreshTokenTimeout.value = setTimeout(refreshTokenAsync, timeout)
  }

  const clearRefreshTokenTimer = () => { clearTimeout(refreshTokenTimeout.value) }

  const refreshTokenAsync = async(force = false) => {
    if (!refreshToken.value) {
      return logoutAsync()
    }

    try {
      while (refreshTokenLock.value && Date.now() - refreshTokenLock.value < 5000) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      if (isTokenExpired() || force) {
        acquireRefreshTokenLock()

        const response = await authApi.refreshTokenAsync({ refresh: refreshToken.value })

        await setTokenAsync(response.data, true)
        releaseRefreshTokenLock()
      } else {
        await startRefreshTokenTimerAsync()
      }
    } catch (err) {
      console.error(err)
      refreshToken.value = null
      return logoutAsync(false)
    }
  }

  const loginAsync = async(username, password) => {
    if (isLogged()) {
      return router.push(menuRoutesEnum.HOME)
    }

    try {
      const response = await authApi.loginApi(username, password)
      await setTokenAsync(response.data)
      await fetchSubscriptions()
      return router.push(menuRoutesEnum.HOME)
    } catch (err) {
      console.error('Login failed:', err)
      throw err
    }
  }

  const registerAsync = async(username, password, firstName, lastName, phoneNumber, email) => {
    if (isLogged()) {
      return router.push(menuRoutesEnum.HOME)
    }

    try {
      const res = await authApi.registerApi(username, password, firstName, lastName, phoneNumber, email)
      return res
    } catch (err) {
      throw err
    }
  }

  const fetchUserData = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await authApi.fetchUserDataApi(accessToken.value)
      userData.value = response.data

      // await localizationStore.setLanguageAsync(userData.value.default_language)

      await fetchSubscriptions()
    } catch (err) {
      console.error('Failed to fetch user data:', err)
    }
  }

  const fetchUserLimits = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await authApi.fetchUserLimitsAsync()
      userLimits.value = response.data
    } catch (err) {
      console.error('Failed to fetch user limits:', err)
    }
  }

  const updateUserData = async(newUserData) => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await authApi.updateUserDataApi(accessToken.value, newUserData)
      userData.value = response.data
    } catch (err) {
      console.error('Failed to update user data:', err)
    }
  }

  const deleteAccountAsync = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      await authApi.deleteAccountAsync(accessToken.value)
    } catch (err) {
      console.error('Failed to delete account:', err)
      throw err
    }
  }
  const fetchSubscriptions = async() => {
    if (!isLogged()) {
      return
    }

    try {
      const response = await getUserSubscriptionsAsync()
      subscriptions.value = Array.isArray(response.data) ? response.data : (response.data.results || [])
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err)
      subscriptions.value = []
    }
  }

  const sendPasswordResetAsync = async(email) => {
    if (!email) {
      return
    }

    try {
      await authApi.sendPasswordResetAsync(email)
    } catch (err) {
      console.error('Failed to send password reset:', err)
      throw err
    }
  }

  const resetPasswordAsync = async(token, newPassword) => {
    if (!token || !newPassword) {
      return
    }

    try {
      await authApi.resetPasswordAsync(token, newPassword)
      return router.push(menuRoutesEnum.LOGIN)
    } catch (err) {
      throw err
    }
  }

  const sendValidationEmailAsync = async(email) => {
    if (!email) {
      return
    }
    const response = await authApi.sendValidationEmailAsync(email)
    return response
  }

  const validateEmailAsync = async(token, userId) => {
    if (!token || !userId) {
      return
    }

    try {
      await authApi.validateEmailAsync(token, userId)
      return router.push(menuRoutesEnum.LOGIN)
    } catch (err) {
      throw err
    }
  }
  const fetchMessagesAsync = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await scheduledApi.fetchScheduledMessagesAsync()
      scheduledMessages.value = response.data.results || []
    } catch (err) {
      console.error('Failed to fetch messages:', err)
      return null
    }
  }

  const toggleScheduledMessageActive = async(messageId, isActive) => {
    if (!isLogged()) {
      return null
    }

    try {
      await scheduledApi.updateScheduledMessageStatusAsync(messageId, isActive)

      const msg = scheduledMessages.value.find(m => m.id === messageId)
      if (msg) {
        msg.active = isActive
        msg.failure_reason = null
      }
    } catch (err) {
      console.error('Failed to update scheduled message status:', err)
      throw err
    }
  }

  const deleteScheduledMessageAsync = async(messageId) => {
    if (!isLogged()) {
      return null
    }

    try {
      await scheduledApi.deleteScheduledMessageAsync(messageId)
      scheduledMessages.value = scheduledMessages.value.filter(m => m.id !== messageId)
    } catch (err) {
      console.error('Failed to delete scheduled message:', err)
      throw err
    }
  }

  const getNextScheduledMessagesPollingTime = () => {
    const activeMessages = scheduledMessages.value.filter(m => m.active && m.scheduled_time)
    if (activeMessages.length === 0) return null

    const nextRuns = activeMessages.map(m => new Date(m.scheduled_time).getTime())
    return Math.min(...nextRuns)
  }

  const formatDate = (date, locale, timeZone = 'UTC') => {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone
    }).format(new Date(date))
  }

  const computeNextRun = (isActive, cronExpr, scheduledTime, timeZone = 'UTC', locale, numRuns = 1) => {
    if (!isActive) return null
    if (scheduledTime) {
      const dates = [formatDate(scheduledTime, locale, timeZone)]
      return dates
    } else if (cronExpr) {
      try {
        const iter = parser.parseExpression(cronExpr, { tz: timeZone })
        const dates = []
        for (let i = 0; i < numRuns; i++) {
          dates.push(iter.next().toDate())
        }

        return dates.map(date => formatDate(date, locale, timeZone))
      } catch (e) {
        console.error('Error computing next run:', e)
        return '-'
      }
    }
    return null
  }

  const fetchAgentsAsync = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await agentsApi.fetchAgentsAsync()
      agents.value = response.data.results || []
    } catch (err) {
      console.error('Failed to fetch agents:', err)
      return null
    }
  }

  const getAgentName = (agentId) => {
    const agent = agents.value.find(a => a.id === agentId)
    if (!agent) return '-'
    return `${agent.name} - ${agent.associated_phone_number?.number || ''}`
  }

  const fetchAgentTopicsAndLanguages = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const [topicsResponse, languagesResponse] = await Promise.all([
        agentsApi.fetchTopicsAsync(),
        agentsApi.fetchLanguagesAsync()
      ])
      agent_topics.value = topicsResponse.data.results || []
      agent_languages.value = languagesResponse.data.results || []
    } catch (err) {
      console.error('Failed to fetch agent topics and languages:', err)
      return null
    }
  }

  const fetchMaxPhoneNumberChanges = async() => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await agentsApi.fetchMaxPhoneNumberChangesAsync()
      max_phone_number_changes.value = response.data.max_changes || 0
    } catch (err) {
      console.error('Failed to fetch max phone number changes:', err)
      return null
    }
  }

  const getAppSettingAsync = async(key) => {
    if (!isLogged()) {
      return null
    }

    try {
      const response = await portalApi.fetchAppSettingAsync(key)

      return response.data.results[0].value
    } catch (err) {
      console.error(`Failed to fetch app setting for key ${key}:`, err)
      return null
    }
  }

  const updateVersionAsync = async() => {
    let currentVersion

    currentVersion = await getAppSettingAsync('VERSION')
    if (!currentVersion) {
      currentVersion = window.config.DEFAULT_VERSION
    }
    if (!version.value || version.value !== currentVersion) {
      version.value = currentVersion
      return true
    }

    return false
  }

  return {
    version,
    accessToken,
    refreshToken,
    userData,
    userLimits,
    viewInfoBanner,
    subscriptions,
    hasActiveSubscription,
    activeSubscription,
    pausedSubscription,
    hasWarmupService,
    agents,
    scheduledMessages,
    agent_topics,
    agent_languages,
    max_phone_number_changes,
    isPastDueSubscription,
    isPausedSubscription,
    hasSchedulingService,

    menuItems,
    loginAsync,
    logoutAsync,
    isLogged,
    isTokenExpired,
    startRefreshTokenTimerAsync,
    refreshTokenAsync,
    registerAsync,
    fetchUserData,
    fetchUserLimits,
    fetchSubscriptions,
    sendPasswordResetAsync,
    sendValidationEmailAsync,
    resetPasswordAsync,
    validateEmailAsync,
    updateUserData,
    deleteAccountAsync,
    fetchMessagesAsync,
    toggleScheduledMessageActive,
    deleteScheduledMessageAsync,
    getNextScheduledMessagesPollingTime,
    computeNextRun,
    fetchAgentsAsync,
    getAgentName,
    fetchAgentTopicsAndLanguages,
    fetchMaxPhoneNumberChanges,
    getAppSettingAsync,
    updateVersionAsync,
  }
}, {
  persist: { enabled: true },
  pick: ['accessToken', 'refreshToken', 'viewInfoBanner', 'version'],
})
